import { FC, useCallback, useMemo } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  collapsedRespondentCount,
  formatPercentTick,
  getLikertScaleColors,
  getLineSeriesColors,
  getSeriesColors,
  statementRespondentCount,
} from 'utils/util/reportChartUtils';
import { NO_RESPONSE_COLOR } from 'views/Widgets/Charts/chartColors';
import HorizontalBar from 'views/Widgets/Charts/HorizontalBar';
import SurveyTrendLineChart from 'views/Widgets/Charts/SurveyTrendLineChart';
import EmptyState from 'views/Widgets/EmptyState';
import {
  type SurveyChartBucket,
  type SurveyChartConfigBody,
  type SurveyChartData,
  type SurveyChartResultData,
  type SurveyChartSeries,
  type SurveyResponsesData,
} from '../consts';
import { getCompactInstanceLabels } from '../instanceLabels';
import SurveyResponsesTable from './SurveyResponsesTable';

interface Props {
  config: SurveyChartConfigBody;
  data: SurveyChartResultData;
}

const RespondentCount: FC<{ denominator: number; answered: number }> = ({
  denominator,
  answered,
}) => (
  <div className="text-muted small text-center mt-2">
    {answered < denominator ? (
      <FormattedMessage
        id="app.views.administration.custom_surveys.charts.renderer.respondent_count_partial"
        defaultMessage="{answered} of {denominator, plural, one {# respondent} other {# respondents}} responded"
        values={{ answered, denominator }}
      />
    ) : (
      <FormattedMessage
        id="app.views.administration.custom_surveys.charts.renderer.respondent_count"
        defaultMessage="{count, plural, one {# respondent} other {# respondents}}"
        values={{ count: denominator }}
      />
    )}
  </div>
);

interface Distribution {
  denominator: number;
  answered: number;
  keys: string[];
  labels: string[];
  percentages: number[];
  colors: string[];
}

// Shared by every distribution chart below - a colored swatch + label +
// percentage row per category, so every chart type shows the same legend
// content instead of leaning on chart.js's own bare (no percentage) legend
// for some and a custom list for others.
const DistributionLegend: FC<{
  keys: string[];
  labels: string[];
  colors: string[];
  // Omitted when one legend is shared across several distributions (see
  // StatementHorizontalBarDistribution) - a single shared legend can only
  // show the color/label key, since the percentage differs per distribution.
  percentages?: number[];
  isDoughnutLegend?: boolean;
}> = ({ keys, labels, colors, percentages, isDoughnutLegend }) => (
  <div className="mt-2" style={{ minWidth: isDoughnutLegend ? '50%' : '100%' }}>
    {labels.map((label, i) => (
      <div key={keys[i]} className="d-flex align-items-center gap-2">
        <span
          style={{
            display: 'inline-block',
            width: '0.75rem',
            height: '0.75rem',
            backgroundColor: colors[i],
          }}
        />
        <span className="flex-grow-1">{label}</span>
        {percentages && (
          <span className="fw-bold">{formatPercentTick(percentages[i])}</span>
        )}
      </div>
    ))}
  </div>
);

const DoughnutDistribution: FC<{
  distribution: Distribution;
  isMultipleStatement?: boolean;
}> = ({ distribution, isMultipleStatement = false }) => {
  const { formatMessage } = useIntl();

  // Chart.js normalizes each slice as value / sum(data), so it always draws
  // a *complete* ring from whatever values it's given - it has no notion of
  // "percent of 100" on its own. Series percentages are computed against the
  // shown-count denominator (see custom_survey_analytics.py), so when fewer
  // people answered than were shown, they legitimately sum to less than 100
  // and the ring would otherwise fill in as if everyone had answered.
  // Padding with an explicit "no response" slice keeps the ring's fill
  // proportional to the true audience, not just to those who answered.
  const noResponseCount = Math.max(
    0,
    distribution.denominator - distribution.answered
  );
  // Rounded to 1 decimal to match the precision of the backend-computed
  // series percentages it's displayed alongside (see _percentage() in
  // custom_survey_analytics.py).
  const noResponsePercentage =
    noResponseCount > 0
      ? Math.round((noResponseCount / distribution.denominator) * 1000) / 10
      : 0;

  // A synthetic key for the "no response" slice - it has no value_key of its
  // own (it isn't a real answer option), but DistributionLegend still needs
  // something stable to key that row on.
  const keys =
    noResponseCount > 0
      ? [...distribution.keys, '__no_response__']
      : distribution.keys;
  const labels =
    noResponseCount > 0
      ? [
          ...distribution.labels,
          formatMessage({
            id: 'app.views.administration.custom_surveys.charts.renderer.no_response',
            defaultMessage: 'No response',
          }),
        ]
      : distribution.labels;
  const data =
    noResponseCount > 0
      ? [...distribution.percentages, noResponsePercentage]
      : distribution.percentages;
  const colors =
    noResponseCount > 0
      ? [...distribution.colors, NO_RESPONSE_COLOR]
      : distribution.colors;

  return (
    <div
      style={{ maxWidth: '320px', margin: '0 auto' }}
      className="f-flex justify-content-center"
    >
      <Doughnut
        data={{
          labels,
          datasets: [
            {
              data,
              backgroundColor: colors,
              borderColor: '#FFFFFF',
              borderWidth: 1,
            },
          ],
        }}
        options={{
          maintainAspectRatio: true,
          cutoutPercentage: 65,
          tooltips: {
            // Default tooltip mode ('nearest') breaks ties between elements
            // that both pass the intersect check by center-point distance -
            // and a 0% slice's arc collapses to a single point sitting right
            // on the seam where the one real (100%) slice starts and ends, so
            // hovering anywhere near that seam picks the zero slice as
            // "nearest" over the real one wrapped all the way around behind
            // it. 'point' only returns arcs the cursor is actually inside, no
            // distance tie-break, so the real slice wins everywhere on the ring.
            mode: 'point',
            callbacks: {
              label: (item, d) => {
                const value = d.datasets?.[0]?.data?.[item.index ?? -1] ?? 0;
                return `${value}%`;
              },
            },
          },
        }}
      />
      {!isMultipleStatement && (
        <DistributionLegend
          keys={keys}
          labels={labels}
          colors={colors}
          percentages={data}
          isDoughnutLegend
        />
      )}
    </div>
  );
};

const HorizontalBarDistribution: FC<{ distribution: Distribution }> = ({
  distribution,
}) => (
  <>
    <HorizontalBar
      data={distribution.percentages.map((p, i) => ({
        value: p / 100,
        color: distribution.colors[i],
      }))}
    />
    <DistributionLegend
      keys={distribution.keys}
      labels={distribution.labels}
      colors={distribution.colors}
      percentages={distribution.percentages}
    />
  </>
);

const BarDistribution: FC<{ distribution: Distribution }> = ({
  distribution,
}) => (
  <div style={{ height: '260px' }}>
    <Bar
      data={{
        labels: distribution.labels,
        datasets: [
          {
            data: distribution.percentages,
            backgroundColor: distribution.colors,
          },
        ],
      }}
      options={{
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (item) => formatPercentTick(Number(item.yLabel)),
          },
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                max: 100,
                callback: formatPercentTick,
              },
            },
          ],
          xAxes: [{ gridLines: { display: false } }],
        },
      }}
    />
  </div>
);

// A multi-statement LIKERT question's BAR chart: one x-axis category per
// statement (chartData.buckets), one dataset per scale value (series) - the
// same shape SurveyTrendLineChart uses for its time axis, just grouped bars
// instead of lines since there's no ordering between statements to connect.
const StatementBarDistribution: FC<{
  buckets: SurveyChartBucket[];
  series: SurveyChartSeries[];
}> = ({ buckets, series }) => {
  const { formatMessage } = useIntl();
  const statementIndicator = (i: number) => `Q${i + 1}`;
  const colors = getLikertScaleColors(series);
  const statementCounts = buckets.map((bucket, i) =>
    statementRespondentCount(bucket, series, i)
  );
  const collapsed = collapsedRespondentCount(statementCounts);
  return (
    <>
      <div style={{ height: '260px' }}>
        <Bar
          data={{
            labels: buckets.map((_, i) => statementIndicator(i)),
            datasets: series.map((s, idx) => ({
              label: s.value_label,
              data: s.percentages,
              backgroundColor: colors[idx],
              // Closes the gap between the bars within one statement's
              // group (its 5 Likert values), so they read as one solid
              // distribution rather than separate bars - per-dataset here
              // since scales.xAxes.barPercentage is deprecated.
              barPercentage: 1,
            })),
          }}
          options={{
            maintainAspectRatio: false,
            legend: { display: true, position: 'bottom' },
            // mode: 'index' + intersect: false so hovering anywhere over a
            // statement's group of bars shows every value's line together
            // (not just the one bar directly under the cursor) - the full
            // breakdown, right on the chart itself instead of a separate
            // popup anchored elsewhere.
            tooltips: {
              mode: 'index',
              intersect: false,
              callbacks: {
                title: (items) => buckets[items[0]?.index ?? -1]?.label ?? '',
                label: (item) => {
                  const valueLabel =
                    series[item.datasetIndex ?? -1]?.value_label ?? '';
                  return `${valueLabel}: ${formatPercentTick(Number(item.yLabel))}`;
                },
              },
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    max: 100,
                    callback: formatPercentTick,
                  },
                },
              ],
              xAxes: [{ gridLines: { display: false } }],
            },
          }}
        />
      </div>
      {/* Each statement's own respondent count sits next to its key entry
          here rather than under the chart as a whole - unlike Doughnut/
          Legend, this grouped bar has no per-statement sub-block to attach
          it to. Omitted per-row when every statement has the same count
          (see collapsedRespondentCount) - shown once below instead.
          Unlike Doughnut/Legend, a suppressed statement's bars aren't
          separately hidden here (chart.js renders one shared canvas for
          every statement's group, and the backend already zeroes a
          suppressed statement's counts - see SurveyChartBucket - so its
          bars are flat/empty on their own); only the count needs the same
          "not enough responses" treatment those two chart types already
          give a suppressed statement. */}
      <div className="small mt-2">
        {buckets.map((bucket, i) => (
          <div
            key={bucket.key}
            className="d-flex justify-content-between align-items-center gap-3"
          >
            <span>
              <strong>{statementIndicator(i)}</strong>:{' '}
              <span>{bucket.label}</span>
            </span>
            {bucket.suppressed ? (
              <div className="custom-survey-chart-suppressed">
                <EmptyState
                  isMini
                  title={formatMessage({
                    id: 'app.views.administration.custom_surveys.charts.responses_table.suppressed',
                    defaultMessage:
                      'Not enough responses to display anonymously.',
                  })}
                />
              </div>
            ) : (
              !collapsed && (
                <RespondentCount
                  denominator={statementCounts[i].denominator}
                  answered={statementCounts[i].answered}
                />
              )
            )}
          </div>
        ))}
      </div>
      {collapsed && (
        <RespondentCount
          denominator={collapsed.denominator}
          answered={collapsed.answered}
        />
      )}
    </>
  );
};

// A multi-statement LIKERT question's Doughnut chart: one ring per
// statement, sharing one legend underneath all of them - the exact same
// value_key/value_label/color legend applies to every ring, only the
// percentages differ, so each ring renders bare (DoughnutDistribution has no
// legend of its own) and hovering a slice still shows that ring's own
// percentage via chart.js's tooltip. Only LIKERT ever has more than one
// statement (see _multi_statement_objects in custom_survey_analytics.py),
// so this always uses the sentiment gradient, matching each ring's own
// colors in `distributions` (also LIKERT, via getSeriesColors).
const StatementDoughnutDistribution: FC<{
  buckets: SurveyChartBucket[];
  series: SurveyChartSeries[];
  distributions: Distribution[];
}> = ({ buckets, series, distributions }) => {
  const { formatMessage } = useIntl();
  const colors = getLikertScaleColors(series);
  const collapsed = collapsedRespondentCount(
    distributions.map((d) => ({
      denominator: d.denominator,
      answered: d.answered,
    }))
  );

  return (
    <div className="d-flex flex-column gap-4">
      {buckets.map((bucket, i) => (
        <div key={bucket.key}>
          <div className="fw-bold text-center mb-2">{bucket.label}</div>
          {bucket.suppressed ? (
            <div className="custom-survey-chart-suppressed">
              <EmptyState
                title={formatMessage({
                  id: 'app.views.administration.custom_surveys.charts.responses_table.suppressed',
                  defaultMessage:
                    'Not enough responses to display anonymously.',
                })}
              />
            </div>
          ) : (
            <>
              <DoughnutDistribution
                distribution={distributions[i]}
                isMultipleStatement
              />
              {/* Omitted per-statement when every statement has the same
                  count (see collapsedRespondentCount) - shown once below
                  the legend instead. */}
              {!collapsed && (
                <RespondentCount
                  denominator={distributions[i].denominator}
                  answered={distributions[i].answered}
                />
              )}
            </>
          )}
        </div>
      ))}
      <DistributionLegend
        keys={series.map((s) => s.value_key)}
        labels={series.map((s) => s.value_label)}
        colors={colors}
      />
      {collapsed && (
        <RespondentCount
          denominator={collapsed.denominator}
          answered={collapsed.answered}
        />
      )}
    </div>
  );
};

// A multi-statement LIKERT question's Legend (HORIZONTAL_BAR) chart: every
// statement shares the exact same value_key/value_label/color legend - only
// the percentages differ - so showing the full legend under every one of
// them just repeats the same swatches and labels N times. Show it once,
// then stack each statement's bare proportional bar underneath; hovering a
// segment shows that statement's percentage for that value (native title
// tooltip - see HorizontalBar's `label`).
// Only LIKERT ever has more than one statement (see
// _multi_statement_objects in custom_survey_analytics.py), so this always
// uses the sentiment gradient.
const StatementHorizontalBarDistribution: FC<{
  buckets: SurveyChartBucket[];
  series: SurveyChartSeries[];
}> = ({ buckets, series }) => {
  const { formatMessage } = useIntl();
  const colors = getLikertScaleColors(series);
  const statementCounts = buckets.map((bucket, i) =>
    statementRespondentCount(bucket, series, i)
  );
  const collapsed = collapsedRespondentCount(statementCounts);

  return (
    <div className="d-flex flex-column gap-3">
      {buckets.map((bucket, i) => (
        <div key={bucket.key}>
          <div className="fw-bold mb-1">{bucket.label}</div>
          {bucket.suppressed ? (
            <div className="custom-survey-chart-suppressed">
              <EmptyState
                title={formatMessage({
                  id: 'app.views.administration.custom_surveys.charts.responses_table.suppressed',
                  defaultMessage:
                    'Not enough responses to display anonymously.',
                })}
              />
            </div>
          ) : (
            <>
              <HorizontalBar
                data={series.map((s, si) => ({
                  value: (s.percentages[i] ?? 0) / 100,
                  color: colors[si],
                  // Just the percentage - the value's label is already
                  // covered by the shared legend below.
                  label: formatPercentTick(s.percentages[i] ?? 0),
                }))}
              />
              {/* Omitted per-statement when every statement has the same
                  count (see collapsedRespondentCount) - shown once below
                  the legend instead. */}
              {!collapsed && (
                <RespondentCount
                  denominator={statementCounts[i].denominator}
                  answered={statementCounts[i].answered}
                />
              )}
            </>
          )}
        </div>
      ))}
      <DistributionLegend
        keys={series.map((s) => s.value_key)}
        labels={series.map((s) => s.value_label)}
        colors={colors}
      />
      {collapsed && (
        <RespondentCount
          denominator={collapsed.denominator}
          answered={collapsed.answered}
        />
      )}
    </div>
  );
};

// config.chartType is authoritative here (see SurveyChartView below); LINE
// and RESPONSES are handled by earlier guards and never reach this map.
const DISTRIBUTION_CHARTS: Record<
  'DOUGHNUT' | 'HORIZONTAL_BAR' | 'BAR',
  FC<{ distribution: Distribution }>
> = {
  DOUGHNUT: DoughnutDistribution,
  HORIZONTAL_BAR: HorizontalBarDistribution,
  BAR: BarDistribution,
};

// Pure render surface for one chart, given data that's already been fetched
// in a batch for a saved report's charts - see CustomSurveyReportBuilder.
const SurveyChartView: FC<Props> = ({ config, data }) => {
  const { formatMessage, locale } = useIntl();

  // config.chartType is the authoritative discriminant (mirrors how the
  // backend decides which of compute_responses_from_dataset /
  // compute_chart_data_from_dataset to run - see compute_report_chart_data),
  // not data's own shape.
  const isResponses = config.chartType === 'RESPONSES';

  // One Distribution per bucket - almost always just bucket 0 (the single
  // collapsed distribution), except for a multi-statement LIKERT question's
  // GROUP_BY_SINGLE chart, where each bucket is a statement (see
  // _multi_statement_objects in custom_survey_analytics.py) and every one of
  // them needs its own distribution.
  const distributions = useMemo(() => {
    if (!data || isResponses) return null;
    const chartData = data as SurveyChartData;

    if (!chartData.series.length) return null;
    return chartData.buckets.map((bucket, i) => ({
      denominator: bucket.denominator,
      answered: chartData.series.reduce(
        (sum, s) => sum + (s.counts[i] ?? 0),
        0
      ),
      keys: chartData.series.map((s) => s.value_key),
      labels: chartData.series.map((s) => s.value_label),
      percentages: chartData.series.map((s) => s.percentages[i] ?? 0),
      colors: getSeriesColors(chartData.question.type, chartData.series),
    }));
  }, [data, isResponses]);

  // The values a trend's lines stand for, in their line colours, for the
  // tooltip and the lines themselves.
  //
  // Built here rather than at the render site below: a fresh array each render
  // would invalidate the chart's own memo every time and hand chart.js new
  // options mid-tooltip.
  const breakdown = useMemo(() => {
    const chartData = data as SurveyChartData | undefined;
    if (!chartData || isResponses) return undefined;
    const entries = chartData.series;
    if (!entries.length) return undefined;
    const colors = getLineSeriesColors(chartData.question.type, entries);
    return entries.map((entry, i) => ({
      value_key: entry.value_key,
      value_label: entry.value_label,
      counts: entry.counts,
      color: colors[i],
    }));
  }, [data, isResponses]);

  // A by-instance trend names its points in the viewer's own timezone, on the
  // axis and in the tooltip alike. Memoized for the same reason as breakdown
  // above.
  const trendBuckets = useMemo(() => {
    const buckets = (data as SurveyChartData | undefined)?.buckets ?? [];
    if (!buckets.every((b) => b.scheduled_datetime)) return buckets;
    const labels = getCompactInstanceLabels(
      buckets.map((b) => b.scheduled_datetime as string),
      locale
    );
    return buckets.map((b, i) => ({ ...b, label: labels[i] }));
  }, [data, locale]);

  // The same wording RespondentCount gives the doughnut.
  const formatRespondedLabel = useCallback(
    (answered: number, denominator: number) =>
      answered < denominator
        ? formatMessage(
            {
              id: 'app.views.administration.custom_surveys.charts.renderer.respondent_count_partial',
              defaultMessage:
                '{answered} of {denominator, plural, one {# respondent} other {# respondents}} responded',
            },
            { answered, denominator }
          )
        : formatMessage(
            {
              id: 'app.views.administration.custom_surveys.charts.renderer.respondent_count',
              defaultMessage:
                '{count, plural, one {# respondent} other {# respondents}}',
            },
            { count: denominator }
          ),
    [formatMessage]
  );

  if (!data) return null;

  if (isResponses) {
    return <SurveyResponsesTable data={data as SurveyResponsesData} />;
  }

  const chartData = data as SurveyChartData;

  if (chartData.buckets.every((b) => b.denominator === 0)) {
    return (
      <EmptyState
        title={formatMessage({
          id: 'app.views.administration.custom_surveys.charts.renderer.no_responses',
          defaultMessage: 'No responses yet for this question.',
        })}
      />
    );
  }

  if (chartData.is_trend || config.chartType === 'LINE') {
    // With nothing left to plot, a line of gaps says less than the same
    // placeholder every other chart type shows for this.
    if (chartData.buckets.every((b) => b.suppressed)) {
      return (
        <div className="custom-survey-chart-suppressed">
          <EmptyState
            title={formatMessage({
              id: 'app.views.administration.custom_surveys.charts.responses_table.suppressed',
              defaultMessage: 'Not enough responses to display anonymously.',
            })}
          />
        </div>
      );
    }
    return (
      <>
        {chartData.question.statement_label && (
          <div className="text-muted small mb-2">
            {chartData.question.statement_label}
          </div>
        )}
        <SurveyTrendLineChart
          buckets={trendBuckets}
          series={chartData.series}
          suppressedLabel={formatMessage({
            id: 'app.views.administration.custom_surveys.charts.responses_table.suppressed',
            defaultMessage: 'Not enough responses to display anonymously.',
          })}
          emptyLabel={formatMessage({
            id: 'app.views.administration.custom_surveys.charts.renderer.no_responses',
            defaultMessage: 'No responses yet for this question.',
          })}
          noResponseLabel={formatMessage({
            id: 'app.views.administration.custom_surveys.charts.renderer.no_response',
            defaultMessage: 'No response',
          })}
          formatRespondedLabel={formatRespondedLabel}
          breakdown={breakdown}
        />
      </>
    );
  }

  if (!distributions) return null;
  // Reaching here with more than one bucket means a non-trend chart (the
  // is_trend guard above already returned) whose buckets are per-statement,
  // not per-time - the only case that happens today (see
  // _multi_statement_objects in custom_survey_analytics.py). Each statement
  // gets its own suppression check and its own respondent count, since one
  // statement in a question can fall below the anonymity floor while others
  // don't.
  if (chartData.buckets.length > 1) {
    switch (config.chartType) {
      case 'BAR':
        return (
          <StatementBarDistribution
            buckets={chartData.buckets}
            series={chartData.series}
          />
        );
      case 'HORIZONTAL_BAR':
        return (
          <StatementHorizontalBarDistribution
            buckets={chartData.buckets}
            series={chartData.series}
          />
        );
      default:
        // Only DOUGHNUT is left (BAR/HORIZONTAL_BAR handled above).
        return (
          <StatementDoughnutDistribution
            buckets={chartData.buckets}
            series={chartData.series}
            distributions={distributions}
          />
        );
    }
  }

  // A single-bucket (non-trend) chart has exactly one bucket - if it's
  // suppressed (some people answered, but fewer than the anonymity floor),
  // rendering the distribution would reveal exactly what a small audience
  // answered (e.g. a 100% slice identifying one person). Same protection
  // SurveyResponsesTable already applies to the RESPONSES chart type.
  if (chartData.buckets[0]?.suppressed) {
    return (
      <div className="custom-survey-chart-suppressed">
        <EmptyState
          title={formatMessage({
            id: 'app.views.administration.custom_surveys.charts.responses_table.suppressed',
            defaultMessage: 'Not enough responses to display anonymously.',
          })}
        />
      </div>
    );
  }

  const distribution = distributions[0];
  if (!distribution) return null;

  const Chart =
    DISTRIBUTION_CHARTS[config.chartType as keyof typeof DISTRIBUTION_CHARTS] ??
    BarDistribution;
  return (
    <>
      <Chart distribution={distribution} />
      <RespondentCount
        denominator={distribution.denominator}
        answered={distribution.answered}
      />
    </>
  );
};

export default SurveyChartView;
